import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, CalendarCheck, Mail } from "lucide-react";
import { CONTACT, whatsappHref } from "../../config/contact";

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const methods = [
  {
    id: "schedule",
    label: "Schedule a Consultation",
    href: CONTACT.bookingUrl,
    icon: CalendarCheck,
    external: true,
    color: "#1F5EFF",
  },
  {
    id: "whatsapp",
    label: "Chat on WhatsApp",
    href: whatsappHref(),
    icon: WhatsAppIcon,
    external: true,
    color: "#128C4B",
  },
  {
    id: "email",
    label: "Email Us",
    href: `mailto:${CONTACT.generalEmail}`,
    icon: Mail,
    external: false,
    color: "#6D28D9",
  },
];

export const ContactWidget = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const firstLink = panelRef.current?.querySelector("a");
    firstLink?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    const onClick = (e) => {
      if (!panelRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) {
        close(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, close]);

  return (
    <div className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            id="contact-widget-panel"
            data-testid="contact-widget-panel"
            role="region"
            aria-label="Contact options"
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
            className="w-[280px] rounded-2xl border border-[#0B1F3A]/10 bg-white shadow-[0_24px_70px_-24px_rgba(11,31,58,0.4)] overflow-hidden"
          >
            <div className="px-5 pt-4 pb-3 bg-[#0B1F3A]">
              <div className="text-white font-display font-bold text-[15px]">How can we help?</div>
              <div className="text-white/70 text-[12px] mt-0.5">We usually respond within one business day.</div>
            </div>
            <ul className="p-2">
              {methods.map((m) => {
                const Icon = m.icon;
                return (
                  <li key={m.id}>
                    <a
                      data-testid={`contact-widget-${m.id}`}
                      href={m.href}
                      {...(m.external
                        ? { target: "_blank", rel: "noopener noreferrer", "aria-label": `${m.label} (opens in new tab)` }
                        : {})}
                      className="flex items-center gap-3 rounded-xl px-3 py-3 text-[14px] font-semibold text-[#0B1F3A] hover:bg-[#F0F8FF] focus-visible:bg-[#F0F8FF] transition-colors"
                    >
                      <span
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg shrink-0"
                        style={{ backgroundColor: `${m.color}14`, color: m.color }}
                      >
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      {m.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        ref={triggerRef}
        data-testid="contact-widget-toggle"
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        aria-controls="contact-widget-panel"
        className="inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] text-white pl-4 pr-5 py-3 text-[14px] font-semibold shadow-[0_16px_40px_-16px_rgba(11,31,58,0.55)] hover:bg-[#1F5EFF] transition-colors"
      >
        {open ? <X className="h-4.5 w-4.5" aria-hidden="true" /> : <MessageSquare className="h-4.5 w-4.5" aria-hidden="true" />}
        {open ? "Close" : "Contact Us"}
      </button>
    </div>
  );
};
